import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  X, 
  Search, 
  MapPin, 
  Star, 
  Clock,
  Info,
  Lightbulb,
  ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';

const HighlightsManager = ({ 
  selectedHighlights = [], 
  onHighlightsChange, 
  discoveredPOIs = [], 
  walkCoordinates = null,
  title = "Walk Highlights" 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showRegularForm, setShowRegularForm] = useState(false);
  const [showCulturalForm, setShowCulturalForm] = useState(false);
  
  // Regular highlight form
  const [regularHighlight, setRegularHighlight] = useState({
    title: '',
    description: ''
  });

  // Cultural POI form
  const [customPOI, setCustomPOI] = useState({
    name: '',
    description: '',
    category: '',
    location_description: '',
    cultural_significance: '',
    insider_tips: ''
  });

  const categories = [
    { value: 'food', label: 'Food & Drink', icon: '🍕' },
    { value: 'art', label: 'Art & Galleries', icon: '🎨' },
    { value: 'architecture', label: 'Architecture', icon: '🏛️' },
    { value: 'business', label: 'Local Business', icon: '🛍️' },
    { value: 'museum', label: 'Museums', icon: '🏛️' },
    { value: 'viewpoint', label: 'Viewpoints', icon: '🌆' },
    { value: 'cultural_venue', label: 'Cultural Venues', icon: '🎭' },
    { value: 'hidden_gem', label: 'Hidden Gems', icon: '💎' }
  ];

  const mapPlaceTypes = (types) => {
    if (types.includes('restaurant') || types.includes('food') || types.includes('cafe') || types.includes('bar')) return 'food';
    if (types.includes('museum')) return 'museum';
    if (types.includes('art_gallery')) return 'art';
    if (types.includes('store') || types.includes('shopping_mall') || types.includes('clothing_store')) return 'business';
    if (types.includes('church') || types.includes('place_of_worship')) return 'cultural_venue';
    if (types.includes('park') || types.includes('natural_feature')) return 'viewpoint';
    if (types.includes('lodging') || types.includes('establishment')) return 'hidden_gem';
    return 'hidden_gem';
  };

  const searchPOIs = async (term) => {
    if (term.length < 2) return;

    setIsSearching(true);
    try {
      const params = new URLSearchParams({ query: term });
      if (walkCoordinates) {
        params.set('location', `${walkCoordinates.lat},${walkCoordinates.lng}`);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/places/search?${params}`
      );
      const mapped = (response.data?.places || []).map((place) => {
        const category = mapPlaceTypes(place.types || []);
        return {
          id: place.place_id,
          name: place.name,
          description: place.formatted_address,
          category,
          subcategory: (place.types || [])[0]?.replace(/_/g, '-') || 'place',
          user_ratings: place.rating || 0,
          source: 'google_places',
        };
      });
      setSearchResults(mapped);
    } catch (error) {
      console.error('Error searching places:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const debounceTimer = setTimeout(() => {
        searchPOIs(searchTerm);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const addHighlight = (highlight) => {
    const existingIds = selectedHighlights.map(h => h.id);
    if (!existingIds.includes(highlight.id)) {
      onHighlightsChange([...selectedHighlights, highlight]);
    }
    setSearchTerm('');
    setShowSearch(false);
  };

  const removeHighlight = (highlightId) => {
    onHighlightsChange(selectedHighlights.filter(h => h.id !== highlightId));
  };

  const updateHighlightField = (highlightId, field, value) => {
    onHighlightsChange(
      selectedHighlights.map(h => h.id === highlightId ? { ...h, [field]: value } : h)
    );
  };

  const updateHighlightFields = (highlightId, fields) => {
    onHighlightsChange(
      selectedHighlights.map(h => h.id === highlightId ? { ...h, ...fields } : h)
    );
  };

  const addRegularHighlight = () => {
    if (!regularHighlight.title.trim()) return;

    const newHighlight = {
      id: `regular-${Date.now()}`,
      type: 'regular',
      title: regularHighlight.title,
      description: regularHighlight.description,
      icon: '📍'
    };

    onHighlightsChange([...selectedHighlights, newHighlight]);
    
    // Reset form
    setRegularHighlight({ title: '', description: '' });
    setShowRegularForm(false);
  };

  const addCustomPOI = () => {
    if (!customPOI.name.trim()) return;

    const newPOI = {
      id: `cultural-${Date.now()}`,
      type: 'cultural',
      title: customPOI.name,
      description: customPOI.description,
      category: customPOI.category,
      location_description: customPOI.location_description,
      cultural_significance: customPOI.cultural_significance,
      insider_tips: customPOI.insider_tips,
      is_custom: true,
      subcategory: 'custom',
      icon: categories.find(c => c.value === customPOI.category)?.icon || '📍'
    };

    onHighlightsChange([...selectedHighlights, newPOI]);
    
    // Reset form
    setCustomPOI({
      name: '',
      description: '',
      category: '',
      location_description: '',
      cultural_significance: '',
      insider_tips: ''
    });
    setShowCulturalForm(false);
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.icon : '📍';
  };

  return (
    <Card className="border-l-4 border-l-amber-500">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-amber-600">
          {title}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search className="h-4 w-4 mr-1" />
              Search POIs
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowRegularForm(!showRegularForm)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Regular
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowCulturalForm(!showCulturalForm)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Cultural
            </Button>
          </div>
        </CardTitle>
        <p className="text-sm text-gray-600">
          Discover local culture, hidden gems, and must-see spots along your route
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Discovered POIs (for LiveTrackingPage) */}
        {discoveredPOIs.length > 0 && (
          <div>
            <Label className="text-base font-medium">POIs Discovered During Your Walk</Label>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {discoveredPOIs.map((poi) => {
                const isSelected = selectedHighlights.find(h => h.id === poi.id);
                const highlight = {
                  id: poi.id,
                  type: 'cultural',
                  title: poi.name,
                  description: poi.description,
                  category: poi.category,
                  subcategory: poi.subcategory,
                  cultural_significance: poi.cultural_significance,
                  insider_tips: poi.insider_tips,
                  user_ratings: poi.user_ratings,
                  price_range: poi.price_range,
                  estimated_visit_duration: poi.estimated_visit_duration,
                  icon: getCategoryIcon(poi.category),
                  poi: poi
                };
                
                return (
                  <div 
                    key={poi.id} 
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => isSelected ? removeHighlight(poi.id) : addHighlight(highlight)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{getCategoryIcon(poi.category)}</span>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{poi.name}</p>
                        <p className="text-xs text-gray-500 line-clamp-1">{poi.description}</p>
                        {poi.user_ratings >= 4.0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs">{poi.user_ratings.toFixed(1)}</span>
                          </div>
                        )}
                        {poi.user_ratings > 0 && poi.user_ratings < 4.0 && (
                          <span className="inline-flex text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium mt-1">
                            {poi.user_ratings.toFixed(1)} — Low rating
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        {isSelected && (
                          <Badge variant="default" size="sm">Selected</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search POIs */}
        {showSearch && (
          <div>
            <Label className="text-base font-medium">Search for Cultural POIs</Label>
            <div className="mt-3 relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search for restaurants, art, architecture, businesses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {isSearching && (
                <div className="mt-2 p-3 text-center text-gray-500">
                  Searching Google Places...
                </div>
              )}

              {!isSearching && searchTerm.length >= 2 && searchResults.length === 0 && (
                <div className="mt-2 p-3 text-center text-gray-500 text-sm">
                  No places found for "{searchTerm}"
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-64 overflow-y-auto">
                  {searchResults.map((poi) => {
                    const isSelected = selectedHighlights.find(h => h.id === poi.id);
                    const highlight = {
                      id: poi.id,
                      type: 'cultural',
                      title: poi.name,
                      description: poi.description,
                      category: poi.category,
                      subcategory: poi.subcategory,
                      cultural_significance: poi.cultural_significance,
                      insider_tips: poi.insider_tips,
                      user_ratings: poi.user_ratings,
                      price_range: poi.price_range,
                      estimated_visit_duration: poi.estimated_visit_duration,
                      icon: getCategoryIcon(poi.category),
                      poi: poi
                    };

                    return (
                      <div 
                        key={poi.id}
                        className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => isSelected ? removeHighlight(poi.id) : addHighlight(highlight)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{getCategoryIcon(poi.category)}</span>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{poi.name}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{poi.description}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" size="sm">
                                {poi.subcategory?.replace('-', ' ')}
                              </Badge>
                              {poi.user_ratings >= 4.0 && (
                                <div className="flex items-center gap-1">
                                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                  <span className="text-xs">{poi.user_ratings.toFixed(1)}</span>
                                </div>
                              )}
                              {poi.user_ratings > 0 && poi.user_ratings < 4.0 && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium">
                                  {poi.user_ratings.toFixed(1)} — Low rating
                                </span>
                              )}
                            </div>
                          </div>
                          {isSelected && (
                            <Badge variant="default" size="sm">Selected</Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Regular Highlight */}
        {showRegularForm && (
          <div>
            <Label className="text-base font-medium">Add Regular Highlight</Label>
            <div className="mt-3 space-y-4">
              <div>
                <Label htmlFor="regularTitle">Title *</Label>
                <Input
                  id="regularTitle"
                  placeholder="e.g., Beautiful viewpoint, Historic building"
                  value={regularHighlight.title}
                  onChange={(e) => setRegularHighlight(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              
              <div>
                <Label htmlFor="regularDescription">Description</Label>
                <Textarea
                  id="regularDescription"
                  placeholder="What makes this spot special?"
                  value={regularHighlight.description}
                  onChange={(e) => setRegularHighlight(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={addRegularHighlight}
                  disabled={!regularHighlight.title.trim()}
                >
                  Add Highlight
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRegularForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Cultural POI */}
        {showCulturalForm && (
          <div>
            <Label className="text-base font-medium">Add Cultural Highlight</Label>
            <div className="mt-3 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customName">Name *</Label>
                  <Input
                    id="customName"
                    placeholder="e.g., Historic Mural, Local Coffee Shop"
                    value={customPOI.name}
                    onChange={(e) => setCustomPOI(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="customCategory">Category</Label>
                  <Select 
                    value={customPOI.category} 
                    onValueChange={(value) => setCustomPOI(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="customDescription">Description</Label>
                <Textarea
                  id="customDescription"
                  placeholder="What makes this place special?"
                  value={customPOI.description}
                  onChange={(e) => setCustomPOI(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customSignificance">Cultural Significance</Label>
                  <Textarea
                    id="customSignificance"
                    placeholder="Why is this culturally important?"
                    value={customPOI.cultural_significance}
                    onChange={(e) => setCustomPOI(prev => ({ ...prev, cultural_significance: e.target.value }))}
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label htmlFor="customTips">Insider Tips</Label>
                  <Textarea
                    id="customTips"
                    placeholder="Tips for other walkers"
                    value={customPOI.insider_tips}
                    onChange={(e) => setCustomPOI(prev => ({ ...prev, insider_tips: e.target.value }))}
                    rows={2}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={addCustomPOI}
                  disabled={!customPOI.name.trim()}
                >
                  Add Cultural Highlight
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCulturalForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Selected Highlights */}
        {selectedHighlights.length > 0 && (
          <div>
            <Label className="text-base font-medium">
              Selected Walk Highlights ({selectedHighlights.length})
            </Label>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {selectedHighlights.map((highlight) => (
                <div key={highlight.id} className="p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">{highlight.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{highlight.title}</p>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{highlight.description}</p>
                          
                          {highlight.type === 'cultural' && (
                            <div className="mt-2">
                              <label className="block text-[11px] font-medium text-amber-700 mb-1">
                                Your take on this spot
                              </label>
                              <Textarea
                                placeholder="What makes this place worth a stop? Add your editorial description..."
                                value={highlight.editorial_description || ''}
                                onChange={(e) => updateHighlightField(highlight.id, 'editorial_description', e.target.value)}
                                rows={2}
                                className="text-xs"
                              />
                            </div>
                          )}

                          {highlight.type === 'cultural' && (
                            <div className="space-y-1 mt-2">
                              {highlight.cultural_significance && (
                                <div className="bg-blue-50 border-l-2 border-blue-400 pl-2 py-1">
                                  <div className="flex items-center gap-1 text-blue-800">
                                    <Info className="h-3 w-3" />
                                    <span className="text-xs font-medium">Cultural Significance</span>
                                  </div>
                                  <p className="text-xs text-blue-700 mt-0.5 line-clamp-2">{highlight.cultural_significance}</p>
                                </div>
                              )}
                              
                              {highlight.insider_tips && (
                                <div className="bg-green-50 border-l-2 border-green-400 pl-2 py-1">
                                  <div className="flex items-center gap-1 text-green-800">
                                    <Lightbulb className="h-3 w-3" />
                                    <span className="text-xs font-medium">Insider Tip</span>
                                  </div>
                                  <p className="text-xs text-green-700 mt-0.5 line-clamp-2">{highlight.insider_tips}</p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 mt-2">
                            <select
                              value={highlight.category || (highlight.type === 'regular' ? 'highlight' : 'hidden_gem')}
                              onChange={(e) => {
                                const newCat = e.target.value;
                                updateHighlightFields(highlight.id, {
                                  category: newCat,
                                  type: newCat === 'highlight' ? 'regular' : 'cultural',
                                  subcategory: newCat,
                                });
                              }}
                              className="text-xs border border-gray-200 rounded-md px-1.5 py-0.5 bg-white hover:border-blue-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                            >
                              <option value="highlight">Highlight</option>
                              <option value="hidden_gem">Hidden Gem</option>
                              <option value="food">Food & Drink</option>
                              <option value="viewpoint">Point of Interest</option>
                              <option value="architecture">Architecture</option>
                              <option value="art">Art</option>
                              <option value="museum">Museum</option>
                              <option value="cultural_venue">Cultural Venue</option>
                              <option value="shop">Shop</option>
                            </select>
                            {highlight.user_ratings >= 4.0 && (
                              <div className="flex items-center gap-1">
                                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                <span className="text-xs">{highlight.user_ratings.toFixed(1)}</span>
                              </div>
                            )}
                            {highlight.user_ratings > 0 && highlight.user_ratings < 4.0 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-medium">
                                {highlight.user_ratings.toFixed(1)} — Low
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeHighlight(highlight.id)}
                          className="text-gray-400 hover:text-red-500 h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedHighlights.length === 0 && !showSearch && !showRegularForm && !showCulturalForm && (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No walk highlights added yet</p>
            <p className="text-xs mt-1">Use the buttons above to add regular highlights or search for cultural POIs</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HighlightsManager;